import React, { useEffect } from 'react'
import ColorDot from '@components/atoms/ColorDot'
import { createPortal } from 'react-dom'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose()
        }
      }

      document.addEventListener('keydown', handleKeyDown)

      return () => {
        document.body.style.overflow = ''
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-expanded="true"
    >
      <div className="relative mx-2 mt-22.5 rounded-lg scale-in lg:mt-0 lg:mx-0 lg:max-w-screen-lg">
        <div className="absolute rounded-t-lg z-2 top-0 left-0 right-0 bg-gradient-metal border-grey-2">
          <div className="flex items-center gap-x-1 py-1.5 px-1 md:p-1.5">
            <ColorDot colorType="red" onClick={onClose} aria-label="Close Modal" />
            <ColorDot colorType="gray" />
            <ColorDot colorType="gray" />
          </div>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

export default Modal
